<template>
  <v-dialog
    value="isDialogOpen"
    max-width="600px"
    scrollable
    persistent
    retain-focus
    @keydown.esc="closeDialog"
  >
    <!-- pass through activator slot -->
    <template v-slot:activator="slotData">
      <slot v-if="$scopedSlots.activator" name="activator" v-bind="slotData" />
    </template>

    <v-form ref="form" v-model="valid">
      <v-card v-if="hasSubscribers" :loading="isLoading">
        <v-card-title>
          Send an Alert
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col class="pt-0">
                <v-textarea
                  v-model="message"
                  label="Message"
                  style="min-height: 60px; resize: none;"
                  :rules="messageRules"
                  outlined
                  clearable
                  auto-grow
                  autofocus
                >
                  <template v-slot:append>
                    <div
                      class="theme--light d-flex flex-column flex-grow-1 align-content-end caption pl-2"
                      style="width: 60px;"
                    >
                      <span>{{ charCount }} {{ charCountLabel }}</span>
                      <span>{{ msgCount }} {{ msgCountLabel }}</span>
                    </div>
                  </template>
                </v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            color="secondary"
            :disabled="isLoading"
            text
            @click="closeDialog"
          >
            Cancel
          </v-btn>
          <ConfirmDialog
            title="Send alert to all subscribers?"
            :message="confirmSendMessage"
            :value="isConfirmOpen"
            ok-button-text="SEND"
            @confirm="send"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                :disabled="!valid || isLoading"
                text
                v-on="on"
              >
                <v-icon>mdi-send</v-icon>
                Send
              </v-btn>
            </template>
          </ConfirmDialog>
        </v-card-actions>
      </v-card>
      <v-card v-else>
        <v-card-title
          >There aren't any users subscribed to the alert service.</v-card-title
        >
        <v-card-text>
          You can add subscribers manually on the Users screen.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="closeDialog">Close</v-btn>
          <v-btn text :to="{ name: 'UserEdit', params: { userId: 'new' } }"
            >Add Subscriber</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
let Logger = require("@/services/Logger")
import ConfirmDialog from "@/components/ConfirmDialog";
import { mapActions } from "vuex";
import store from "@/store/store";

export default {
  name: "SendDialog",
  components: {
    ConfirmDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isConfirmOpen: false,
    isDialogOpen: true,
    valid: false,
    isLoading: false,
    message: "",
    messageRules: [(v) => !!v || "A message is required."],
    subscriberCount: 0,
  }),
  computed: {
    charCount() {
      return this.message?.length ?? 0;
    },
    charCountLabel() {
      return this.charCount === 1 ? "char" : "chars";
    },
    msgCount() {
      return parseInt(this.charCount / 160, 10) + (this.charCount > 0 ? 1 : 0);
    },
    msgCountLabel() {
      return this.msgCount === 1 ? "msg" : "msgs";
    },
    hasSubscribers() {
      // If null, assume there are some subscribers. Don't want to delay sending of a
      // potentially emergency alert for a failed request to count the subscribers
      return this.subscriberCount === null || this.subscriberCount > 0;
    },
    subscriberCountLabel() {
      return this.subscriberCount === 1 ? "recipient" : "recipients";
    },
    confirmSendMessage() {
      if (this.subscriberCount === null) {
        return `<strong>The message will be sent to all opted-in subscribers.</strong> 
                Once you confirm, it is not possible to stop delivery of the message.`;
      }

      return `<strong>The message will be sent to ${this.subscriberCount} opted-in 
              ${this.subscriberCountLabel}.</strong> Once you confirm, it is not 
              possible to stop delivery of the message.`;
    },
  },
  methods: {
    closeDialog() {
      this.isDialogOpen = false;
      this.$router.push({ name: "History" });
      this.$emit("input", false);
    },
    send() {
      try {
        this.isLoading = true;

        this.createMessage(this.message)
          .then(() => {
            this.showSnackbar({ message: "Message sent.", type: "success" });
            this.closeDialog();
          })
          .catch((err) => {
            this.showSnackbar({ message: err.message, type: "error" });
          });
      } catch (err) {
        Logger.error(err)
      } finally {
        this.isLoading = false;
      }
    },
    ...mapActions("messaging", ["createMessage"]),
    ...mapActions("snackbar", ["showSnackbar"]),
  },
  beforeRouteEnter(to, from, next) {
    const loadDataPromise = new Promise((resolve, reject) => {
      store
        .dispatch("messaging/getSubscriberCount")
        .then((result) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });

    next((vm) => {
      vm.isLoading = true;

      loadDataPromise
        .then((result) => {
          vm.subscriberCount = result.data.subscriberCount;
        })
        .catch((err) => {
          vm.subscriberCount = null;
          store.dispatch('snackbar/showSnackbar', { message: err.message, color: "error" });
        })
        .finally(() => {
          vm.isLoading = false;
        });
    });
  },
};
</script>

<style lang="scss" scoped>
.v-input ::v-deep {
  .v-input__control {
    .v-input__slot {
      .v-text-field__slot {
        textarea {
          resize: none;
        }
      }
    }
  }
}
</style>
